/**
 * @file     message-registry.js
 * @author   Wes Garland, wes@distributive.network
 * @date     Nov 2023
 *
 * The message registry is used to track DcpMessages, event loop references, and send promises. 
 * Registries are kept on a per-Connection basis.
 *
 * Entries in the registry corresponding to Requests stay in the registry until the matching Response
 * has been received, or the connection is closed. Entries corresponding to Responses stay in the
 * registry until the matching Ack has been received, or the connection is closed. This gives us the
 * behaviour we want at the API level; node programs with no other event loop references "stay alive"
 * while a dialogue is underway, but once the dialogue has concluded, the regular Node.js semantics
 * are free to let the program exit.
 *
 * I have purposefully chosen to not expose accessors which can locate messages in the registry by using
 * their message id. The reason for this is to force the calling code to use better accessors; message
 * id is not unique between message types (only the <type,id> tuple is unique), and it is available in
 * too many places; eg. the network representation instead of the actual object.
 */
'use strict';

const { assert }     = require('dcp/common/dcp-assert');
const { DcpMessage } = require('./dcp-message');

exports.DialogueRegistry = DialogueRegistry;

const debugging = require('dcp/debugging').scope('dcp');

/**
 * DialogueRegistryEntry constructor. These entries track DcpMessages, their send promises, and event
 * loop references.
 *
 * One registry entry is created for each Request->Response or Response->Ack "dialogue". We
 * automatically add an event loop reference on construction, unless the message has been explicitly
 * unrefd. Any reference is removed when the entry is removed from the registry. This gives us the
 * behaviour that DcpMessage dialogue references have a lifespan which begins when the message is
 * enqueued and concludes just before but on the same tick as the send promise resolution.
 *
 * @param {Object}  message      instance of DcpMessage
 * @param {Object}  promiseHnd   a handle for the promise that was returned from Connection::send; this
 *                               object must be populated with resolve and reject properties before
 *                               the message is transmitted to the peer.
 * @constructor
 */
function DialogueRegistryEntry(message, promiseHnd, sendCallerStack)
{
  assert(message instanceof DcpMessage);
  assert(typeof promiseHnd === 'object' || message.type === 'batch');

  this.message         = message;
  this.promiseHnd      = promiseHnd;
  this.sendCallerStack = sendCallerStack;
}

/**
 * DialogueRegistry constructor.  A collection of registry entries, indexible by the actual message
 * object, and if the message object is a Request, its message id. We don't index Response messages by
 * message id, because a) we don't need to, and b) message ids are only unique per message type; i.e. 
 * requests and responses have matching ids.
 *
 * @param {object} connection       instance of Connection; the connection this registry is for
 * @constructor
 */
function DialogueRegistry(connection)
{
  this.connection = connection;
  this.requestDict = {};
  this.messageMap = new Map();
  Object.seal(this.messageMap);
}

/**
 * Add a message and its dialogue-promise to the registry.
 *
 * @param {Object} message          instance of DcpMessage to add to the registry
 * @param {Object} promiseHnd       a handle for the promise that was returned from Connection::send
 * @param {String} sendCallerStack  a string that represents a stack that includes Connection::send
 *
 * @returns {Object} the registry entry that was just added
 */
DialogueRegistry.prototype.add = function DialogueRegistry$$add(message, promiseHnd, sendCallerStack)
{
  assert(!this.messageMap.has(message));

  debugging('registry') && console.debug(this.connection.debugLabel, `registry - adding ${message.type} ${message.id}`);
  const entry = new DialogueRegistryEntry(message, promiseHnd, sendCallerStack);
  this.messageMap.set(message, entry);
  if (message instanceof this.connection.Request)
    this.requestDict[message.id] = entry;
  if (message.hasRef() === undefined)
    message.ref(); /* Add a dialogue-duration reference unless we've been asked not to */

  return entry;
}

/**
 * Remove an entry from the registry
 * @param {Object}  message      instance of DcpMessage corresponding to the registry entry to remove
 */
DialogueRegistry.prototype.remove = function DialogueRegistry$$remove(message)
{
  const entry = this.locate(message);

  debugging('registry') && console.debug(this.connection.debugLabel, `registry - removing ${message.type} ${message.id}`);
  this.messageMap.delete(message);
  message.unref();

  if (message instanceof this.connection.Request)
    delete this.requestDict[message.id];
}

/**
 * Locate a registry entry for a given message.
 * @param {Object}  message      instance of DcpMessage whose registry entry we want to find
 * @returns {Object} instanceof DialogueRegistryEntry
 */
DialogueRegistry.prototype.locate = function DialogueRegistry$$locate(message)
{
  return this.messageMap.get(message);
}

/**
 * Search the registry for a request message that matches a response.
 *
 * @param {Object}   response   instanceof Response
 * @returns {Object} instanceof Request
 */
DialogueRegistry.prototype.getRequest = function DialogueRegistry$$getRequest(response)
{
  const request = this.requestDict[response.id].message;
  assert(response instanceof this.connection.Response);
  assert(request instanceof this.connection.Request);
  return request;
}

/**
 * Rejecting unresolved send-promises via the promise handles in the registry.
 *
 * @param {Error}    rejectError - the error object to reject the unresolved promises with
 * @param {string}   [optional] type - if specified, only clear that type.
 */
DialogueRegistry.prototype.reject = function DialogueRegistry$$reject(rejectError, type)
{
  assert(rejectError instanceof Error);
  assert(type === 'batch' || type === 'request' || type === 'response' || type === undefined);

  debugging('registry') && console.debug(this.connection.debugLabel, `registry - clearing ${this.messageMap.size} entries`);

  for (let entry of this.messageMap.values())
  {
    if (entry.promiseHnd?.reject && (!type || entry.message.type === type))
      entry.promiseHnd.reject(rejectError);
  }
}

/**
 * Clear a message registry. This removes all the entries in the registy, rejecting any unresolved
 * send-promises via the promise handles in the registry.
 *
 * @param {Error}    rejectError - the error object to reject the unresolved promises with
 */
DialogueRegistry.prototype.clear = function DialogueRegistry$$clear(rejectError)
{
  assert(rejectError instanceof Error);

  debugging('registry') && console.debug(this.connection.debugLabel, `registry - clearing ${this.messageMap.size} entries`);

  for (let entry of this.messageMap.values())
  {
    this.remove(entry.message);
    if (entry.promiseHnd?.reject)
      entry.promiseHnd.reject(rejectError);
  }
}

DialogueRegistry.prototype.getDialoguePromises = function DialogueRegistry$$getDialoguePromises()
{
  debugging('registry') && console.debug(this.connection.debugLabel, `registry - returning ${this.messageMap.size} promises`);
  return Array.from(this.messageMap.values()).map(entry => entry.promiseHnd.promise);
}

DialogueRegistry.prototype.isEmpty = function DialogueRegistry$$isEmpty()
{
  return this.messageMap.size === 0
}
